import { Extension } from '@tiptap/core';
import type { NodeType, ResolvedPos } from '@tiptap/pm/model';
import { EditorState, NodeSelection, Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { Decoration, DecorationSet, type EditorView } from '@tiptap/pm/view';

import { TEMPLATE_FIELD_INLINE_NAME } from '../../doc-engine/templateFields';
import { isAtFieldEdge, isFieldInCard, TDirection } from '../nodes/formField/formFieldHelpers';

/** Whether a paragraph field sits right beside a caret outside it, in `dir`. */
const hasParagraphFieldBeside = (type: NodeType, $caret: ResolvedPos, dir: TDirection): boolean => {
  const field = dir > 0 ? $caret.nodeAfter : $caret.nodeBefore;
  return field?.type === type && !isFieldInCard($caret.doc, dir > 0 ? $caret.pos : $caret.pos - field.nodeSize);
};

/** Where an arrow in `dir` takes a caret at a paragraph field's edge: out to just beside the field, or back in. */
const crossEdge = (type: NodeType, { selection }: EditorState, dir: TDirection): number | undefined => {
  if (!(selection instanceof TextSelection) || !selection.empty) return undefined;
  const $caret = selection.$from;
  if ($caret.parent.type !== type) return hasParagraphFieldBeside(type, $caret, dir) ? $caret.pos + dir : undefined;
  if (!isAtFieldEdge($caret, dir) || isFieldInCard($caret.doc, $caret.before())) return undefined;
  return dir > 0 ? $caret.after() : $caret.before();
};

/** Where a click at `pos` beside a paragraph field goes: just inside the field when it landed on the field's box. */
const getClickedPos = (view: EditorView, type: NodeType, pos: number, target: EventTarget | null): number => {
  const $pos = view.state.doc.resolve(pos);
  const isOnField = (fieldPos: number) => target instanceof Node && !!view.nodeDOM(fieldPos)?.contains(target);
  if (hasParagraphFieldBeside(type, $pos, 1) && isOnField(pos)) return pos + 1;
  if (hasParagraphFieldBeside(type, $pos, -1) && isOnField(pos - $pos.nodeBefore!.nodeSize)) return pos - 1;
  return pos;
};

// ProseMirror's own zero-size separator: with it between them, the browser keeps the two stops of an edge apart.
const createSeparator = () => {
  const separator = document.createElement('img');
  separator.className = 'ProseMirror-separator';
  return separator;
};

const separatorAt = (pos: number, isFieldStart: boolean) =>
  Decoration.widget(pos, createSeparator, {
    side: isFieldStart ? 1 : -1,
    key: isFieldStart ? 'separator-before-field' : 'separator-after-field',
  });

/** A separator just outside each paragraph-field edge the caret is at, from inside the field or beside it. */
const getEdgeSeparators = (type: NodeType, $caret: ResolvedPos): Decoration[] => {
  if ($caret.parent.type !== type) {
    return [
      ...(hasParagraphFieldBeside(type, $caret, -1) ? [separatorAt($caret.pos, false)] : []),
      ...(hasParagraphFieldBeside(type, $caret, 1) ? [separatorAt($caret.pos, true)] : []),
    ];
  }
  if (isFieldInCard($caret.doc, $caret.before())) return [];
  return [
    ...(isAtFieldEdge($caret, -1) ? [separatorAt($caret.before(), true)] : []),
    ...(isAtFieldEdge($caret, 1) ? [separatorAt($caret.after(), false)] : []),
  ];
};

// The browser treats the stops just inside and just outside a paragraph field's edge as one stop.
const ParagraphFieldCaret = Extension.create({
  name: 'paragraphFieldCaret',

  addKeyboardShortcuts() {
    const moveCaret = (dir: TDirection) => {
      const target = crossEdge(this.editor.schema.nodes[TEMPLATE_FIELD_INLINE_NAME], this.editor.state, dir);
      return target !== undefined && this.editor.commands.setTextSelection(target);
    };
    return { ArrowLeft: () => moveCaret(-1), ArrowRight: () => moveCaret(1) };
  },

  addProseMirrorPlugins() {
    const type = this.editor.schema.nodes[TEMPLATE_FIELD_INLINE_NAME];
    return [
      new Plugin({
        key: new PluginKey('paragraphFieldCaret'),
        props: {
          decorations({ selection, doc }) {
            if (!(selection instanceof TextSelection) || !selection.empty) return null;
            const separators = getEdgeSeparators(type, selection.$from);
            return separators.length ? DecorationSet.create(doc, separators) : null;
          },

          handleDOMEvents: {
            // selectFieldOnEdgeClick just selected the field; unfocused, the browser swaps that for a caret beside it.
            mousedown(view, event) {
              const { selection, doc } = view.state;
              const isEdgePress =
                selection instanceof NodeSelection &&
                selection.node.type === type &&
                view.nodeDOM(selection.from) === event.target &&
                !isFieldInCard(doc, selection.from);
              if (isEdgePress) view.focus();
              return false;
            },
          },

          // Beside a field, the caret goes outside it; on its box, inside, where its popup opens.
          handleClick(view, pos, event) {
            const { selection, doc } = view.state;
            const $pos = doc.resolve(pos);
            if (!(selection instanceof TextSelection)) return false;
            if (!hasParagraphFieldBeside(type, $pos, -1) && !hasParagraphFieldBeside(type, $pos, 1)) return false;
            const clickedPos = getClickedPos(view, type, pos, event.target);
            // A click on the box stays the field's when the caret is already inside: the default would follow `pos`.
            if (selection.from === clickedPos) return clickedPos !== pos;
            view.dispatch(view.state.tr.setSelection(TextSelection.create(doc, clickedPos)));
            return true;
          },
        },
      }),
    ];
  },
});

export default ParagraphFieldCaret;
